import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PrivateLayout from "../../layouts/PrivateLayout";
import {
  fetchCurrentRaidTeam,
  fetchRaidFightCasts,
  fetchRaidFightDeaths,
  fetchRaidFightParticipants,
  fetchRaidReportByCode,
} from "../../api/api";
import useDecodedToken from "../../utils/useDecodedToken";
import {
  attachParsePercents,
  combineParsePercents,
  formatAmount,
  formatDateTime,
  formatFightTime,
  formatParsePercent,
  formatRankPercent,
  getClassColor,
  getRankPercentStyle,
  getDifficultyLabel,
  getRoleColor,
  getRoleIcon,
} from "./raidStatsUtils";

const sortByAmountDesc = (rows) =>
  [...rows].sort((a, b) => {
    const amountA = a.amount ?? -Infinity;
    const amountB = b.amount ?? -Infinity;
    return amountB - amountA;
  });

const splitPerformancesByMetric = (performances) => {
  const healing = performances.filter(
    (p) => (p.metric || "").toLowerCase() === "hps"
  );
  const dps = performances.filter(
    (p) => (p.metric || "").toLowerCase() !== "hps"
  );
  return { dps: sortByAmountDesc(dps), healing: sortByAmountDesc(healing) };
};

const keyOf = (p) => `${p.characterName?.toLowerCase()}-${p.realm?.toLowerCase()}`;

const METRIC_TAB_LABELS = { dps: "DPS", healing: "Healing" };

const TAB_COLOR_CLASSES = {
  red: "bg-red-500/20 text-red-300 border border-red-500/40",
  green: "bg-green-500/20 text-green-300 border border-green-500/40",
  gray: "bg-gray-500/20 text-gray-300 border border-gray-500/40",
  blue: "bg-blue-500/20 text-blue-300 border border-blue-500/40",
  purple: "bg-purple-500/20 text-purple-300 border border-purple-500/40",
};

function TabButton({ active, onClick, icon, label, count, color }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
        active
          ? TAB_COLOR_CLASSES[color]
          : "bg-white/5 text-gray-400 border border-transparent hover:bg-white/10"
      }`}
    >
      <i className={icon}></i>
      {label}
      {count !== undefined && <span className="text-gray-500">({count})</span>}
    </button>
  );
}

function SortableHeader({ label, active, dir, onClick, align = "left" }) {
  return (
    <th
      onClick={onClick}
      className={`py-2 pr-3 font-semibold cursor-pointer select-none hover:text-gray-300 ${
        align === "right" ? "text-right" : "text-left"
      }`}
    >
      <span
        className={`inline-flex items-center gap-1 ${align === "right" ? "flex-row-reverse" : ""}`}
      >
        {label}
        <i
          className={`fas fa-sort${active ? (dir === "asc" ? "-up" : "-down") : ""} text-[10px] ${
            active ? "text-white" : "text-gray-600"
          }`}
        ></i>
      </span>
    </th>
  );
}

function sortRows(rows, sortKey, sortDir, comparators) {
  const copy = [...rows];
  copy.sort((a, b) => {
    const result = comparators[sortKey](a, b);
    return sortDir === "asc" ? result : -result;
  });
  return copy;
}

const compareText = (a, b) => (a || "").localeCompare(b || "");
const compareNumber = (a, b) => (a ?? -Infinity) - (b ?? -Infinity);

function PerformanceTable({ title, rows, showSpecRank, getCharacterId, onSelectCharacter }) {
  const [sortKey, setSortKey] = useState("amount");
  const [sortDir, setSortDir] = useState("desc");

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setSortDir(key === "name" ? "asc" : "desc");
    }
  };

  const sortedRows = useMemo(
    () =>
      sortRows(rows, sortKey, sortDir, {
        name: (a, b) => compareText(a.characterName, b.characterName),
        amount: (a, b) => compareNumber(a.amount, b.amount),
      }),
    [rows, sortKey, sortDir]
  );

  return (
    <div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wider text-gray-500 border-b border-white/10">
            <SortableHeader
              label="Character"
              active={sortKey === "name"}
              dir={sortDir}
              onClick={() => handleSort("name")}
            />
            {showSpecRank && (
              <th className="text-left py-2 pr-3 font-semibold">Spec</th>
            )}
            <SortableHeader
              label={title}
              active={sortKey === "amount"}
              dir={sortDir}
              onClick={() => handleSort("amount")}
              align="right"
            />
            {showSpecRank && (
              <th className="text-right py-2 font-semibold">Rank %</th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {sortedRows.map((performance, idx) => {
            const characterId = getCharacterId(performance);
            const color = getClassColor(performance.className);
            return (
              <tr key={`${performance.characterName}-${idx}`}>
                <td className="py-2 pr-3">
                  {characterId ? (
                    <button
                      type="button"
                      onClick={() => onSelectCharacter(characterId)}
                      className="font-medium hover:underline"
                      style={{ color }}
                    >
                      {performance.characterName}
                    </button>
                  ) : (
                    <span className="font-medium" style={{ color }}>
                      {performance.characterName}
                    </span>
                  )}
                  <span className="text-gray-500 text-xs ml-1.5">
                    {performance.realm}
                  </span>
                </td>
                {showSpecRank && (
                  <td className="py-2 pr-3 text-gray-300 capitalize">
                    {performance.spec}
                  </td>
                )}
                <td className="py-2 pr-3 text-right text-white">
                  {formatAmount(performance.amount)}
                </td>
                {showSpecRank && (
                  <td
                    className={`py-2 text-right ${getRankPercentStyle(performance.rankPercent)}`}
                  >
                    {formatRankPercent(performance.rankPercent)}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SummaryTable({ rows, showSpecRank, getCharacterId, onSelectCharacter }) {
  const [sortKey, setSortKey] = useState("parse");
  const [sortDir, setSortDir] = useState("desc");

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setSortDir(key === "name" ? "asc" : "desc");
    }
  };

  const sortedRows = useMemo(
    () =>
      sortRows(rows, sortKey, sortDir, {
        name: (a, b) => compareText(a.characterName, b.characterName),
        amount: (a, b) => compareNumber(a.amount, b.amount),
        parse: (a, b) => compareNumber(a.parsePercent, b.parsePercent),
      }),
    [rows, sortKey, sortDir]
  );

  return (
    <div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wider text-gray-500 border-b border-white/10">
            <SortableHeader
              label="Character"
              active={sortKey === "name"}
              dir={sortDir}
              onClick={() => handleSort("name")}
            />
            {showSpecRank && (
              <th className="text-left py-2 pr-3 font-semibold">Spec</th>
            )}
            <SortableHeader
              label="Output"
              active={sortKey === "amount"}
              dir={sortDir}
              onClick={() => handleSort("amount")}
              align="right"
            />
            <SortableHeader
              label="Parse"
              active={sortKey === "parse"}
              dir={sortDir}
              onClick={() => handleSort("parse")}
              align="right"
            />
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {sortedRows.map((row, idx) => {
            const characterId = getCharacterId(row);
            const color = getClassColor(row.className);
            return (
              <tr key={`${row.characterName}-${idx}`}>
                <td className="py-2 pr-3">
                  <i
                    className={`${getRoleIcon(row.mainRole)} ${getRoleColor(row.mainRole)} text-xs mr-1.5`}
                    title={row.mainRole}
                  ></i>
                  {characterId ? (
                    <button
                      type="button"
                      onClick={() => onSelectCharacter(characterId)}
                      className="font-medium hover:underline"
                      style={{ color }}
                    >
                      {row.characterName}
                    </button>
                  ) : (
                    <span className="font-medium" style={{ color }}>
                      {row.characterName}
                    </span>
                  )}
                  <span className="text-gray-500 text-xs ml-1.5">{row.realm}</span>
                </td>
                {showSpecRank && (
                  <td className="py-2 pr-3 text-gray-300 capitalize">{row.spec}</td>
                )}
                <td className="py-2 pr-3 text-right text-white">
                  {formatAmount(row.amount)}
                  <span className="text-gray-500 text-[10px] uppercase ml-1">
                    {row.metric}
                  </span>
                </td>
                <td
                  className={`py-2 text-right ${getRankPercentStyle(row.parsePercent)}`}
                >
                  {formatParsePercent(row.parsePercent)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DeathsTable({ rows, getColorForCharacter }) {
  const [sortKey, setSortKey] = useState("time");
  const [sortDir, setSortDir] = useState("asc");

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortedRows = useMemo(
    () =>
      sortRows(rows, sortKey, sortDir, {
        name: (a, b) => compareText(a.characterName, b.characterName),
        time: (a, b) => compareNumber(a.secondsIntoFight, b.secondsIntoFight),
      }),
    [rows, sortKey, sortDir]
  );

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-xs uppercase tracking-wider text-gray-500 border-b border-white/10">
          <SortableHeader
            label="Character"
            active={sortKey === "name"}
            dir={sortDir}
            onClick={() => handleSort("name")}
          />
          <SortableHeader
            label="Time"
            active={sortKey === "time"}
            dir={sortDir}
            onClick={() => handleSort("time")}
            align="right"
          />
          <th className="text-left py-2 font-semibold">Killed By</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-white/5">
        {sortedRows.map((death, idx) => (
          <tr key={`${death.characterName}-${idx}`}>
            <td className="py-2 pr-3">
              <span
                className="font-medium"
                style={{
                  color: getColorForCharacter(death.characterName, death.realm),
                }}
              >
                {death.characterName}
              </span>
              <span className="text-gray-500 text-xs ml-1.5">{death.realm}</span>
            </td>
            <td className="py-2 pr-3 text-right text-gray-300">
              {formatFightTime(death.secondsIntoFight)}
            </td>
            <td className="py-2 text-gray-400">
              Killed by{" "}
              <span className="text-gray-200">
                {death.killedByAbility || "Unknown"}
              </span>
              {death.killedBySource && (
                <>
                  {" "}
                  (<span className="text-gray-200">{death.killedBySource}</span>)
                </>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CastsTable({ rows, getColorForCharacter }) {
  const [sortKey, setSortKey] = useState("time");
  const [sortDir, setSortDir] = useState("asc");

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortedRows = useMemo(
    () =>
      sortRows(rows, sortKey, sortDir, {
        name: (a, b) => compareText(a.characterName, b.characterName),
        time: (a, b) => compareNumber(a.secondsIntoFight, b.secondsIntoFight),
      }),
    [rows, sortKey, sortDir]
  );

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-xs uppercase tracking-wider text-gray-500 border-b border-white/10">
          <SortableHeader
            label="Character"
            active={sortKey === "name"}
            dir={sortDir}
            onClick={() => handleSort("name")}
          />
          <SortableHeader
            label="Time"
            active={sortKey === "time"}
            dir={sortDir}
            onClick={() => handleSort("time")}
            align="right"
          />
          <th className="text-left py-2 font-semibold">Ability</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-white/5">
        {sortedRows.map((cast, idx) => (
          <tr key={`${cast.characterName}-${cast.abilityName}-${idx}`}>
            <td className="py-2 pr-3">
              <span
                className="font-medium"
                style={{
                  color: getColorForCharacter(cast.characterName, cast.realm),
                }}
              >
                {cast.characterName}
              </span>
              <span className="text-gray-500 text-xs ml-1.5">{cast.realm}</span>
            </td>
            <td className="py-2 pr-3 text-right text-gray-300">
              {formatFightTime(cast.secondsIntoFight)}
            </td>
            <td className="py-2 text-gray-200">{cast.abilityName}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RaidReportDetail() {
  const backgroundImage = "/ww-wallpaper.webp";
  const navigate = useNavigate();
  const { code } = useParams();
  const { token } = useDecodedToken();

  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [expandedFightId, setExpandedFightId] = useState(null);
  const [rosterByNameRealm, setRosterByNameRealm] = useState({});
  const [liveParticipantsByFight, setLiveParticipantsByFight] = useState({});
  const [loadingParticipantsFor, setLoadingParticipantsFor] = useState(null);
  const [participantsError, setParticipantsError] = useState({});

  const [deathsByFight, setDeathsByFight] = useState({});
  const [loadingDeathsFor, setLoadingDeathsFor] = useState(null);
  const [deathsError, setDeathsError] = useState({});

  const [activeMetricTabByFight, setActiveMetricTabByFight] = useState({});

  const [castsQueryByFight, setCastsQueryByFight] = useState({});
  const [castsByFight, setCastsByFight] = useState({});
  const [loadingCastsFor, setLoadingCastsFor] = useState(null);
  const [castsError, setCastsError] = useState({});

  const loadReport = useCallback(async () => {
    if (!token || !code) return;
    setIsLoading(true);
    setErrorMessage("");
    try {
      const data = await fetchRaidReportByCode(token, code);
      setReport(data);
    } catch (error) {
      setErrorMessage(error.message || "Failed to load raid report.");
    } finally {
      setIsLoading(false);
    }
  }, [token, code]);

  const loadRoster = useCallback(async () => {
    if (!token) return;
    try {
      const team = await fetchCurrentRaidTeam(token);
      const map = {};
      (team?.members || []).forEach((member) => {
        const key = `${member.characterName?.toLowerCase()}-${member.realm?.toLowerCase()}`;
        map[key] = { characterId: member.characterId, role: member.role };
      });
      setRosterByNameRealm(map);
    } catch (error) {
      // Roster is a nice-to-have for linking performances; ignore failures (e.g. no team yet).
      setRosterByNameRealm({});
    }
  }, [token]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  useEffect(() => {
    loadRoster();
  }, [loadRoster]);

  const fights = useMemo(() => report?.fights || [], [report]);

  const getCharacterId = (performance) => {
    const key = `${performance.characterName?.toLowerCase()}-${performance.realm?.toLowerCase()}`;
    return rosterByNameRealm[key]?.characterId || null;
  };

  // Report-detail performances carry a role directly; live participants never
  // do (WCL's table() only exposes class), so fall back to the roster's
  // configured role. Returns null when there's no real signal (unregistered
  // pug on an unranked pull). Tanks fold into "dps" — no separate tank
  // comparison for now.
  const getAuthoritativeRole = (performance) => {
    if (!performance) return null;
    let role = performance.role ? performance.role.toLowerCase() : null;
    if (!role) role = rosterByNameRealm[keyOf(performance)]?.role?.toLowerCase() || null;
    if (!role) return null;
    return role === "tank" ? "dps" : role;
  };

  // Which of a character's two rows (dps-metric / hps-metric, if both exist)
  // is their "main" one for Summary purposes. dpsMaxAmount/healMaxAmount are
  // the top amount in each metric group for this pull.
  const resolveMainRole = (dpsRow, healRow, dpsMaxAmount, healMaxAmount) => {
    const authoritative = getAuthoritativeRole(dpsRow) || getAuthoritativeRole(healRow);
    if (authoritative) return authoritative;
    if (healRow && !dpsRow) return "healer";
    if (dpsRow && !healRow) return "dps";
    // No real signal and both rows exist (common on unranked/live pulls,
    // where role is always null). Rank percentile alone isn't reliable here —
    // a trivial incidental amount can still land a high percentile in a
    // small/sparse group. Compare magnitude instead: what fraction of the
    // group's top performer they are. A real dps player is a meaningful
    // share of the top dps output while their incidental healing is a sliver
    // of the top healer's output, and vice versa.
    const dpsShare = dpsMaxAmount > 0 ? (dpsRow.amount ?? 0) / dpsMaxAmount : 0;
    const healShare = healMaxAmount > 0 ? (healRow.amount ?? 0) / healMaxAmount : 0;
    return healShare > dpsShare ? "healer" : "dps";
  };

  const loadLiveParticipants = useCallback(
    async (fight) => {
      if (!token || !code) return;
      if (liveParticipantsByFight[fight.wclFightId]) return;
      setLoadingParticipantsFor(fight.wclFightId);
      setParticipantsError((prev) => ({ ...prev, [fight.wclFightId]: "" }));
      try {
        const data = await fetchRaidFightParticipants(
          token,
          code,
          fight.wclFightId
        );
        setLiveParticipantsByFight((prev) => ({
          ...prev,
          [fight.wclFightId]: Array.isArray(data) ? data : [],
        }));
      } catch (error) {
        setParticipantsError((prev) => ({
          ...prev,
          [fight.wclFightId]: error.message || "Failed to load participants.",
        }));
      } finally {
        setLoadingParticipantsFor(null);
      }
    },
    [token, code, liveParticipantsByFight]
  );

  const toggleFight = (fight) => {
    if (expandedFightId === fight.wclFightId) {
      setExpandedFightId(null);
      return;
    }
    setExpandedFightId(fight.wclFightId);
    if (!fight.performances || fight.performances.length === 0) {
      loadLiveParticipants(fight);
    }
  };

  const loadDeaths = useCallback(
    async (wclFightId) => {
      if (!token || !code) return;
      if (deathsByFight[wclFightId]) return;
      setLoadingDeathsFor(wclFightId);
      setDeathsError((prev) => ({ ...prev, [wclFightId]: "" }));
      try {
        const data = await fetchRaidFightDeaths(token, code, wclFightId);
        setDeathsByFight((prev) => ({
          ...prev,
          [wclFightId]: Array.isArray(data) ? data : [],
        }));
      } catch (error) {
        setDeathsError((prev) => ({
          ...prev,
          [wclFightId]: error.message || "Failed to load deaths.",
        }));
      } finally {
        setLoadingDeathsFor(null);
      }
    },
    [token, code, deathsByFight]
  );

  const selectTab = (wclFightId, tab) => {
    setActiveMetricTabByFight((prev) => ({ ...prev, [wclFightId]: tab }));
    if (tab === "deaths") {
      loadDeaths(wclFightId);
    }
  };

  const handleCastsSearch = useCallback(
    async (wclFightId) => {
      const ability = (castsQueryByFight[wclFightId] || "").trim();
      if (!token || !code || !ability) return;
      setLoadingCastsFor(wclFightId);
      setCastsError((prev) => ({ ...prev, [wclFightId]: "" }));
      try {
        const data = await fetchRaidFightCasts(token, code, wclFightId, ability);
        setCastsByFight((prev) => ({
          ...prev,
          [wclFightId]: Array.isArray(data) ? data : [],
        }));
      } catch (error) {
        setCastsError((prev) => ({
          ...prev,
          [wclFightId]: error.message || "Failed to look up ability usage.",
        }));
      } finally {
        setLoadingCastsFor(null);
      }
    },
    [token, code, castsQueryByFight]
  );

  return (
    <PrivateLayout backgroundImage={backgroundImage}>
      <div className="flex flex-col items-center text-white bg-black/50 px-4 py-10 min-h-full">
        <div className="w-full max-w-6xl">
          <button
            type="button"
            onClick={() => navigate("/raid-statistics")}
            className="mb-6 flex items-center gap-2 text-sm text-gray-300 hover:text-white transition-colors"
          >
            <i className="fas fa-arrow-left"></i>
            Back to Raid Statistics
          </button>

          {isLoading && (
            <div className="flex items-center justify-center py-20">
              <div className="w-10 h-10 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}

          {!isLoading && errorMessage && (
            <div className="bg-red-500/20 border border-red-500/40 rounded-lg px-4 py-3 text-red-300 text-sm text-center">
              {errorMessage}
            </div>
          )}

          {!isLoading && !errorMessage && report && (
            <>
              <div className="text-center mb-8">
                <h1 className="text-2xl md:text-4xl font-bold drop-shadow-lg mb-2">
                  {report.title || report.code}
                </h1>
                <p className="text-gray-300 text-sm md:text-base">
                  {report.zoneName} &middot; {formatDateTime(report.startTime)} &ndash;{" "}
                  {formatDateTime(report.endTime)}
                </p>
                <div className="flex items-center justify-center gap-3 mt-3">
                  <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 font-medium">
                    {report.kills} kills
                  </span>
                  <span className="text-xs px-2.5 py-1 rounded-full bg-red-500/20 text-red-300 font-medium">
                    {report.wipes} wipes
                  </span>
                </div>
              </div>

              {fights.length === 0 && (
                <div className="text-center py-16 bg-white/5 border border-white/10 rounded-xl">
                  <p className="text-gray-400">No fights recorded for this report.</p>
                </div>
              )}

              <div className="space-y-3">
                {fights.map((fight) => {
                  const isExpanded = expandedFightId === fight.wclFightId;
                  const reportPerformances = fight.performances || [];
                  const usingLiveParticipants = reportPerformances.length === 0;
                  const livePerformances =
                    liveParticipantsByFight[fight.wclFightId] || [];
                  const performances = usingLiveParticipants
                    ? livePerformances
                    : reportPerformances;
                  const isLoadingLive =
                    usingLiveParticipants &&
                    loadingParticipantsFor === fight.wclFightId;
                  const liveError = participantsError[fight.wclFightId];
                  const { dps: dpsRaw, healing: healingRaw } =
                    splitPerformancesByMetric(performances);
                  const dpsPerformances = attachParsePercents(dpsRaw);
                  const healingPerformances = attachParsePercents(healingRaw);

                  const dpsByKey = {};
                  dpsPerformances.forEach((p) => {
                    dpsByKey[keyOf(p)] = p;
                  });
                  const healingByKey = {};
                  healingPerformances.forEach((p) => {
                    healingByKey[keyOf(p)] = p;
                  });
                  const allSummaryKeys = new Set([
                    ...Object.keys(dpsByKey),
                    ...Object.keys(healingByKey),
                  ]);
                  const dpsMaxAmount = Math.max(
                    0,
                    ...dpsPerformances.map((p) => p.amount || 0)
                  );
                  const healMaxAmount = Math.max(
                    0,
                    ...healingPerformances.map((p) => p.amount || 0)
                  );
                  const summaryRows = Array.from(allSummaryKeys).map((key) => {
                    const dpsRow = dpsByKey[key];
                    const healRow = healingByKey[key];
                    const mainRole = resolveMainRole(
                      dpsRow,
                      healRow,
                      dpsMaxAmount,
                      healMaxAmount
                    );
                    const mainRow = mainRole === "healer" ? healRow || dpsRow : dpsRow || healRow;
                    const otherRow = mainRow === dpsRow ? healRow : dpsRow;
                    return {
                      ...mainRow,
                      mainRole,
                      parsePercent: combineParsePercents(
                        mainRow.parsePercent,
                        otherRow?.parsePercent
                      ),
                    };
                  });

                  const defaultMetricTab = "summary";
                  const activeMetricTab =
                    activeMetricTabByFight[fight.wclFightId] || defaultMetricTab;
                  const activeMetricRows =
                    activeMetricTab === "dps" ? dpsPerformances : healingPerformances;

                  const classByNameRealm = {};
                  performances.forEach((p) => {
                    if (!p.className) return;
                    const key = `${p.characterName?.toLowerCase()}-${p.realm?.toLowerCase()}`;
                    classByNameRealm[key] = p.className;
                  });
                  const getColorForCharacter = (characterName, realm) => {
                    const key = `${characterName?.toLowerCase()}-${realm?.toLowerCase()}`;
                    return getClassColor(classByNameRealm[key]);
                  };

                  const deathsLoaded = !!deathsByFight[fight.wclFightId];
                  const deaths = deathsByFight[fight.wclFightId] || [];
                  const isLoadingDeaths = loadingDeathsFor === fight.wclFightId;
                  const deathsErr = deathsError[fight.wclFightId];

                  const castsQuery = castsQueryByFight[fight.wclFightId] || "";
                  const castsResults = castsByFight[fight.wclFightId];
                  const isLoadingCasts = loadingCastsFor === fight.wclFightId;
                  const castsErr = castsError[fight.wclFightId];

                  return (
                    <div
                      key={fight.wclFightId}
                      className="rounded-xl overflow-hidden border border-white/10"
                    >
                      <button
                        type="button"
                        onClick={() => toggleFight(fight)}
                        className="w-full flex items-center justify-between gap-3 px-4 py-3.5 bg-white/10 hover:bg-white/15 transition-colors text-left"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <i
                            className={`fas ${fight.kill ? "fa-check-circle text-emerald-400" : "fa-skull text-red-400"}`}
                          ></i>
                          <div className="min-w-0">
                            <p className="font-semibold text-sm truncate">
                              {fight.encounterName}
                            </p>
                            <p className="text-xs text-gray-400">
                              {getDifficultyLabel(fight.difficulty)}
                              {!fight.kill &&
                                fight.fightPercentage !== null &&
                                fight.fightPercentage !== undefined &&
                                ` · ${fight.fightPercentage.toFixed(1)}% remaining`}
                            </p>
                          </div>
                        </div>
                        <i
                          className={`fas fa-chevron-${isExpanded ? "up" : "down"} text-gray-500 text-xs flex-shrink-0`}
                        ></i>
                      </button>

                      {isExpanded && (
                        <div className="bg-black/20 px-4 py-3">
                          <div className="flex items-center gap-1 mb-3 flex-wrap">
                            <TabButton
                              active={activeMetricTab === "summary"}
                              onClick={() => selectTab(fight.wclFightId, "summary")}
                              icon="fas fa-list-check"
                              label="Summary"
                              count={summaryRows.length}
                              color="purple"
                            />
                            <TabButton
                              active={activeMetricTab === "dps"}
                              onClick={() => selectTab(fight.wclFightId, "dps")}
                              icon="fas fa-khanda"
                              label="DPS"
                              count={dpsPerformances.length}
                              color="red"
                            />
                            <TabButton
                              active={activeMetricTab === "healing"}
                              onClick={() => selectTab(fight.wclFightId, "healing")}
                              icon="fas fa-heart"
                              label="Healing"
                              count={healingPerformances.length}
                              color="green"
                            />
                            <TabButton
                              active={activeMetricTab === "deaths"}
                              onClick={() => selectTab(fight.wclFightId, "deaths")}
                              icon="fas fa-skull-crossbones"
                              label="Deaths"
                              count={deathsLoaded ? deaths.length : undefined}
                              color="gray"
                            />
                            <TabButton
                              active={activeMetricTab === "casts"}
                              onClick={() => selectTab(fight.wclFightId, "casts")}
                              icon="fas fa-magnifying-glass"
                              label="Casts"
                              count={castsResults ? castsResults.length : undefined}
                              color="blue"
                            />
                          </div>

                          {(activeMetricTab === "summary" ||
                            activeMetricTab === "dps" ||
                            activeMetricTab === "healing") && (
                            <>
                              {isLoadingLive && (
                                <div className="flex items-center justify-center py-6 gap-2 text-sm text-gray-400">
                                  <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                                  Fetching live participant data from WarcraftLogs...
                                </div>
                              )}
                              {!isLoadingLive && liveError && (
                                <p className="text-red-400 text-sm text-center py-4">
                                  {liveError}
                                </p>
                              )}
                              {!isLoadingLive &&
                                !liveError &&
                                performances.length === 0 && (
                                  <p className="text-gray-400 text-sm text-center py-4">
                                    No participant data available for this pull.
                                  </p>
                                )}
                              {!isLoadingLive &&
                                !liveError &&
                                performances.length > 0 && (
                                  <div className="overflow-x-auto">
                                    {usingLiveParticipants && (
                                      <p className="text-xs text-gray-500 mb-2">
                                        <i className="fas fa-bolt mr-1"></i>
                                        Live data from WarcraftLogs — spec and rank %
                                        aren't available for unranked pulls.
                                      </p>
                                    )}
                                    {activeMetricTab === "summary" ? (
                                      summaryRows.length === 0 ? (
                                        <p className="text-gray-400 text-sm text-center py-4">
                                          No performance data for this pull.
                                        </p>
                                      ) : (
                                        <SummaryTable
                                          rows={summaryRows}
                                          showSpecRank={!usingLiveParticipants}
                                          getCharacterId={getCharacterId}
                                          onSelectCharacter={(characterId) =>
                                            navigate(
                                              `/raid-statistics/characters/${characterId}`
                                            )
                                          }
                                        />
                                      )
                                    ) : activeMetricRows.length === 0 ? (
                                      <p className="text-gray-400 text-sm text-center py-4">
                                        No {METRIC_TAB_LABELS[activeMetricTab]} data
                                        for this pull.
                                      </p>
                                    ) : (
                                      <PerformanceTable
                                        title={METRIC_TAB_LABELS[activeMetricTab]}
                                        rows={activeMetricRows}
                                        showSpecRank={!usingLiveParticipants}
                                        getCharacterId={getCharacterId}
                                        onSelectCharacter={(characterId) =>
                                          navigate(
                                            `/raid-statistics/characters/${characterId}`
                                          )
                                        }
                                      />
                                    )}
                                  </div>
                                )}
                            </>
                          )}

                          {activeMetricTab === "deaths" && (
                            <div>
                              {isLoadingDeaths && (
                                <div className="flex items-center justify-center py-6 gap-2 text-sm text-gray-400">
                                  <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                                  Fetching deaths from WarcraftLogs...
                                </div>
                              )}
                              {!isLoadingDeaths && deathsErr && (
                                <p className="text-red-400 text-sm text-center py-4">
                                  {deathsErr}
                                </p>
                              )}
                              {!isLoadingDeaths &&
                                !deathsErr &&
                                deathsLoaded &&
                                deaths.length === 0 && (
                                  <p className="text-gray-400 text-sm text-center py-4">
                                    No deaths recorded for this pull.
                                  </p>
                                )}
                              {!isLoadingDeaths && !deathsErr && deaths.length > 0 && (
                                <div className="overflow-x-auto">
                                  <DeathsTable
                                    rows={deaths}
                                    getColorForCharacter={getColorForCharacter}
                                  />
                                </div>
                              )}
                            </div>
                          )}

                          {activeMetricTab === "casts" && (
                            <div>
                              <form
                                onSubmit={(e) => {
                                  e.preventDefault();
                                  handleCastsSearch(fight.wclFightId);
                                }}
                                className="flex items-center gap-2 mb-3"
                              >
                                <input
                                  type="text"
                                  value={castsQuery}
                                  onChange={(e) =>
                                    setCastsQueryByFight((prev) => ({
                                      ...prev,
                                      [fight.wclFightId]: e.target.value,
                                    }))
                                  }
                                  placeholder="e.g. Healthstone"
                                  className="flex-1 bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-400"
                                />
                                <button
                                  type="submit"
                                  disabled={!castsQuery.trim() || isLoadingCasts}
                                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                  {isLoadingCasts ? (
                                    <i className="fas fa-spinner fa-spin"></i>
                                  ) : (
                                    "Search"
                                  )}
                                </button>
                              </form>

                              {!isLoadingCasts && castsErr && (
                                <p className="text-red-400 text-sm text-center py-2">
                                  {castsErr}
                                </p>
                              )}
                              {!isLoadingCasts &&
                                !castsErr &&
                                castsResults &&
                                castsResults.length === 0 && (
                                  <p className="text-gray-400 text-sm text-center py-2">
                                    No matching ability usage found for this pull.
                                  </p>
                                )}
                              {!isLoadingCasts &&
                                !castsErr &&
                                castsResults &&
                                castsResults.length > 0 && (
                                  <div className="overflow-x-auto">
                                    <CastsTable
                                      rows={castsResults}
                                      getColorForCharacter={getColorForCharacter}
                                    />
                                  </div>
                                )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </PrivateLayout>
  );
}

export default RaidReportDetail;
